import { useDisclosure } from "@mantine/hooks";
import { DateTime } from "luxon";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { SurfaceTreatmentsCsvDetailsModal } from "#modules/company/view/SurfaceTreatmentsCsvDetailsModal";
import { makeCustomSurfaceTreatmentsCsv } from "#modules/companySettings/domain/makeCustomSurfaceTreatmentsCsv";
import { CustomSurfaceTreatmentsDto } from "#modules/companySettings/infra/fetchCustomSurfaceTreatments.api";
import { useUploadCustomSurfaceTreatments } from "#modules/companySettings/view/useUploadCustomSurfaceTreatments";
import { makeDownloadableUrl } from "#shared/helpers/file";
import { Icon } from "#shared/icons/Icon";
import { Anchor } from "#shared/view/components/Anchor";
import { Group } from "#shared/view/components/Group";
import { Button } from "#shared/view/designSystem/Button";
import { FileButton } from "#shared/view/designSystem/FileButton";

type Props = {
  companyId: string;
  customSurfaceTreatments: CustomSurfaceTreatmentsDto | null;
};

export const SurfaceTreatmentsCsvActions = ({ companyId, customSurfaceTreatments }: Props) => {
  const { t } = useTranslation();
  const importButtonRef = useRef<() => void>(null);
  const { uploadCustomSurfaceTreatments } = useUploadCustomSurfaceTreatments();

  const [isHelpModalOpened, { close: closeHelpModal, open: openHelpModal }] = useDisclosure(false);

  const uploadSurfaceTreatmentsCsv = async (file: File | null) => {
    if (!file || !companyId) {
      return;
    }

    uploadCustomSurfaceTreatments({ companyId, file });
    importButtonRef.current?.();
  };

  const makeCsvUrl = () => {
    const csvData = makeCustomSurfaceTreatmentsCsv(customSurfaceTreatments ?? []);

    return makeDownloadableUrl(csvData, "text/csv");
  };

  const downloadButtonLabel =
    !customSurfaceTreatments || customSurfaceTreatments.length === 0
      ? t("settings.surfaceTreatments.upload.downloadTemplate")
      : t("settings.surfaceTreatments.upload.downloadFile");

  return (
    <>
      <Group gap="sm">
        <FileButton
          resetRef={importButtonRef}
          onChange={uploadSurfaceTreatmentsCsv}
          accept="text/csv"
        >
          {(props) => (
            <Button variant="outline" icon={<Icon name="upload" />} {...props}>
              {t("settings.surfaceTreatments.upload.button")}
            </Button>
          )}
        </FileButton>
        <Anchor
          href={makeCsvUrl()}
          target="_blank"
          rel="noopener noreferrer"
          download={`export-fabera-surface-treatments-${DateTime.now().toISODate()}.csv`}
        >
          <Button variant="outline" icon={<Icon name="download" />}>
            {downloadButtonLabel}
          </Button>
        </Anchor>
        <Button variant="subtle" leftSection={<Icon name="help" />} onClick={openHelpModal}>
          {t("settings.surfaceTreatments.upload.helpButton")}
        </Button>
      </Group>

      <SurfaceTreatmentsCsvDetailsModal opened={isHelpModalOpened} close={closeHelpModal} />
    </>
  );
};
